import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import type {
  Service,
  SiteSettingsData,
  StaticPage,
  TeamMember,
} from "@shared/schema";
import { DEFAULT_SITE_SETTINGS } from "@shared/defaults";

/**
 * Fetches the site-wide settings (brand info + page text + CTAs).
 * Falls back to compile-time defaults if the API isn't available
 * yet (initial render, dev without DB, etc.).
 */
export function useSiteSettings(): SiteSettingsData {
  const q = useQuery({
    queryKey: ["site-settings"],
    queryFn: () =>
      api<SiteSettingsData>("/api/site-settings", { withAuth: false }),
    staleTime: 5 * 60_000,
    placeholderData: DEFAULT_SITE_SETTINGS,
  });
  return q.data ?? DEFAULT_SITE_SETTINGS;
}

export function useTeam() {
  return useQuery({
    queryKey: ["team"],
    queryFn: () => api<TeamMember[]>("/api/team", { withAuth: false }),
  });
}

export function useServices() {
  return useQuery({
    queryKey: ["services"],
    queryFn: () => api<Service[]>("/api/services", { withAuth: false }),
  });
}

export function useStaticPage(slug: string | null) {
  return useQuery({
    queryKey: ["page", slug],
    queryFn: () =>
      api<StaticPage>(`/api/pages/${slug}`, { withAuth: false }),
    enabled: !!slug,
  });
}
